import { useState, useEffect } from 'react';
import { setlistsApi } from '@/api/setlists';
import { apiErrorMessage } from '@/utils/apiError';
import type { Setlist } from '@/types/setlist';

interface Props {
  teamId: string;
  songId: string;
  songTitle: string;
  onClose: () => void;
}

export default function AddToSetlistModal({ teamId, songId, songTitle, onClose }: Props) {
  const [setlists, setSetlists] = useState<Setlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setlistsApi.list(teamId).then((res) => {
      setSetlists(res.data);
    }).catch((e) => setError(apiErrorMessage(e))).finally(() => setLoading(false));
  }, [teamId]);

  const handleAdd = async (setlistId: string) => {
    setAdding(setlistId);
    setError('');
    try {
      await setlistsApi.addItem(teamId, setlistId, songId);
      setAdded((prev) => new Set([...prev, setlistId]));
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setAdding(null);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const res = await setlistsApi.create(teamId, { name: newName.trim() });
      const sl = res.data;
      setSetlists((prev) => [sl, ...prev]);
      setNewName('');
      setCreating(false);
      await handleAdd(sl.id);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="bg-[#0D0C09] border border-white/10 rounded-2xl w-full max-w-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div>
            <h3 className="text-white font-medium text-base">Adicionar ao setlist</h3>
            <p className="text-white/40 text-xs mt-0.5 truncate max-w-xs">{songTitle}</p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white/70 transition-colors">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="px-5 py-3 max-h-72 overflow-y-auto">
          {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-5 h-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin"></div>
            </div>
          ) : setlists.length === 0 && !creating ? (
            <p className="text-white/30 text-sm text-center py-6">Nenhum setlist. Crie um abaixo.</p>
          ) : (
            setlists.map((sl) => {
              const isAdded = added.has(sl.id);
              const isAdding = adding === sl.id;
              return (
                <div key={sl.id} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0">
                  <i className="ri-list-check-2 text-white/30 text-sm"></i>
                  <span className="flex-1 text-white text-sm truncate">{sl.name}</span>
                  <button
                    onClick={() => !isAdded && handleAdd(sl.id)}
                    disabled={isAdded || isAdding}
                    className={`shrink-0 px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                      isAdded
                        ? 'bg-green-500/20 text-green-400 cursor-default'
                        : 'bg-[#C8A96E]/20 text-[#C8A96E] hover:bg-[#C8A96E]/30'
                    }`}
                  >
                    {isAdded ? 'Adicionado' : isAdding ? '...' : 'Adicionar'}
                  </button>
                </div>
              );
            })
          )}

          {creating && (
            <div className="flex gap-2 pt-2">
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setCreating(false); }}
                placeholder="Nome do setlist..."
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-[#C8A96E]/50"
              />
              <button onClick={handleCreate} className="px-3 py-1.5 bg-[#C8A96E] text-black text-xs rounded-lg font-medium hover:bg-[#D4B87A]">
                Criar
              </button>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-white/5">
          <button
            onClick={() => setCreating(!creating)}
            className="w-full py-2 rounded-lg border border-white/10 text-white/50 text-sm hover:bg-white/5 hover:text-white/80 transition-colors flex items-center justify-center gap-2"
          >
            <i className="ri-add-line"></i>
            Novo setlist
          </button>
        </div>
      </div>
    </div>
  );
}
